import axios from 'axios';
import {OpenAPI} from 'client';
import {useEffect, useState} from 'react';

interface UseYjsDocumentResult {
  yjsContent: Uint8Array | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to load Yjs binary document content from the Document API
 * @param documentId - The document ID to load (can be a document ID or version ID)
 * @param enabled - Whether to enable the hook (default: true)
 * @returns Object containing yjsContent, isLoading, and error states
 */
export const useYjsDocument = (
  documentId: string | null | undefined,
  enabled: boolean = true
): UseYjsDocumentResult => {
  const [yjsContent, setYjsContent] = useState<Uint8Array | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    const abortController = new AbortController();

    // Clear previous content when document ID changes
    setYjsContent(null);
    setError(null);

    if (!documentId || !enabled) {
      return;
    }

    const loadYjsContent = async () => {
      setIsLoading(true);

      try {
        const response = await axios.get<ArrayBuffer>(
          `${OpenAPI.BASE}/api/v1/document/${documentId}/binary`,
          {
            responseType: 'arraybuffer',
            withCredentials: OpenAPI.WITH_CREDENTIALS,
            signal: abortController.signal,
          }
        );
        if (!active) {
          return;
        }
        const uint8Array = new Uint8Array(response.data);
        setYjsContent(uint8Array);
      } catch (err) {
        if (!active || axios.isCancel(err)) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to load document'));
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadYjsContent();

    return () => {
      active = false;
      abortController.abort();
    };
  }, [documentId, enabled]);

  return {yjsContent, isLoading, error};
};
